// ============================================
// pages/society/SocietyTransfers.tsx
// Society's transfer history (in + out). Initiate transfers from My Consignments.
// ============================================
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { AlertCircle, ArrowRightLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { gunnyTransfersAPI, seasonsAPI } from '../../api/services';
import { Pagination } from '../../components/common/Pagination/Pagination';
import { usePagination } from '../../components/common/Pagination/usePagination';

export const SocietyTransfers: React.FC = () => {
  const { user } = useAuth();
  const [, setSeasonId] = useState<number | null>(null);
  const [transfers, setTransfers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'in' | 'out'>('all');
  const [search, setSearch] = useState<string>('');

  const load = useCallback(async (sid: number) => {
    setLoading(true); setErr(null);
    try {
      const { data } = await gunnyTransfersAPI.list(sid);
      setTransfers(data);
    } catch (e: any) {
      setErr(e?.response?.data?.message || 'Failed to load transfers');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    seasonsAPI.active().then(({ data }) => {
      if (data?.id) { setSeasonId(data.id); load(data.id); }
    }).catch(() => setLoading(false));
  }, [load]);

  const filteredTransfers = useMemo(() => {
    const s = search.trim().toLowerCase();
    return transfers.filter((t: any) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      const out = t.source_pacs_id === user?.pacs_id;
      if (directionFilter === 'in' && out) return false;
      if (directionFilter === 'out' && !out) return false;
      if (s) {
        const counter = out ? (t.destPacs?.name || '') : (t.sourcePacs?.name || '');
        const blob = `${t.id} ${counter} ${t.reason || ''} ${t.rejection_reason || ''}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [transfers, statusFilter, directionFilter, search, user?.pacs_id]);

  const { page, pageSize, totalPages, totalItems, pagedItems, setPage, setPageSize } =
    usePagination(filteredTransfers, 25);

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <ArrowRightLeft className="w-5 h-5 text-emerald-600" /> My Transfers
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Outgoing and incoming transfers between PACS. To start a new transfer, open a consignment in <Link to="/society/consignments" className="text-emerald-700 hover:underline">My Consignments</Link> and click <span className="font-medium">Transfer</span>.
        </p>
      </div>

      <div className="mb-4 bg-white rounded-xl border border-gray-200 p-3 flex flex-wrap gap-2 items-center">
        <select value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value as any)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">Both directions</option>
          <option value="in">Incoming only</option>
          <option value="out">Outgoing only</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded bg-white">
          <option value="all">All statuses</option>
          <option value="requested">Requested</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        <input type="text" placeholder="Search PACS / reason…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 text-sm border rounded flex-1 min-w-[200px]" />
        <span className="text-xs text-gray-500">{filteredTransfers.length} of {transfers.length} transfers</span>
      </div>

      {err && <div className="mb-3 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm"><AlertCircle className="w-4 h-4" /> {err}</div>}

      <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
        {loading ? <div className="p-10 text-center text-gray-500">Loading…</div>
          : transfers.length === 0 ? <div className="p-10 text-center text-gray-500">No transfers yet.</div>
          : filteredTransfers.length === 0 ? <div className="p-10 text-center text-gray-500">No transfers match the current filters.</div>
          : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs uppercase text-gray-600">
                  <th className="px-3 py-2">#</th>
                  <th className="px-3 py-2">Direction</th>
                  <th className="px-3 py-2">Counterparty PACS</th>
                  <th className="px-3 py-2 text-right">Bags</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Reason</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((t: any) => {
                  const out = t.source_pacs_id === user?.pacs_id;
                  const counter = out ? t.destPacs?.name : t.sourcePacs?.name;
                  return (
                    <tr key={t.id} className="border-b last:border-0">
                      <td className="px-3 py-2">{t.id}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${out ? 'bg-rose-50 text-rose-700' : 'bg-emerald-50 text-emerald-700'}`}>
                          {out ? 'Outgoing' : 'Incoming'}
                        </span>
                      </td>
                      <td className="px-3 py-2">{counter || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{t.bags?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-xs">{t.transfer_date || '—'}</td>
                      <td className="px-3 py-2 text-xs text-gray-600">{t.reason || t.rejection_reason || '—'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                          t.status === 'requested' ? 'bg-amber-100 text-amber-700' :
                          t.status === 'rejected' ? 'bg-rose-100 text-rose-700' :
                          t.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
        {!loading && filteredTransfers.length > 0 && totalPages > 1 && (
          <div className="px-3 py-3 border-t bg-gray-50">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              totalItems={totalItems}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              itemLabel="transfers"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SocietyTransfers;
